/**
 * Google Play Billing — JS wrapper around our custom Capacitor plugin.
 *
 * The native plugin is registered in MainActivity.java (Android only) and
 * exposes methods via `registerPlugin('GooglePlayBilling')`. On the web
 * (or iOS, until a StoreKit equivalent is added) every method becomes a
 * no-op that resolves with an empty payload — callers must handle the
 * `available()` check first.
 *
 * Product IDs MUST match the Google Play Console subscription SKUs.
 */
import { Capacitor, registerPlugin } from '@capacitor/core';

export const PRODUCT_MONTHLY = 'matbakhi_premium_monthly';
export const PRODUCT_YEARLY  = 'matbakhi_premium_yearly';
export const ALL_PRODUCTS = [PRODUCT_MONTHLY, PRODUCT_YEARLY];

// `registerPlugin` returns a Proxy. On non-Android platforms the proxy still
// "works" but every method call will throw `UNIMPLEMENTED` — we guard with
// `available()` so the UI can fall back to the Stripe/web flow.
const GooglePlayBilling = registerPlugin('GooglePlayBilling');

export function billingAvailable() {
  try {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
  } catch {
    return false;
  }
}

export async function initBilling() {
  if (!billingAvailable()) return { ok: false, reason: 'not-android' };
  try {
    return await GooglePlayBilling.initialize();
  } catch (e) {
    console.warn('[Billing] initialize failed', e);
    return { ok: false, reason: e?.message || 'init-failed' };
  }
}

/** Returns [{productId, title, price, offerToken, billingPeriod, ...}, ...] */
export async function getProducts(productIds = ALL_PRODUCTS) {
  if (!billingAvailable()) return [];
  try {
    const res = await GooglePlayBilling.getProducts({ productIds });
    return res?.products || [];
  } catch (e) {
    console.warn('[Billing] getProducts failed', e);
    return [];
  }
}

/**
 * Launch the Play Store purchase dialog. Resolves once the user completes
 * (or cancels) the flow. The native plugin acknowledges automatically.
 *
 * @returns purchase object { productId, purchaseToken, orderId, acknowledged, ... }
 *          or null if the user cancelled / something went wrong.
 */
export async function purchase(productId, offerToken) {
  if (!billingAvailable()) return null;
  try {
    const res = await GooglePlayBilling.purchase({ productId, offerToken });
    return res?.purchase || null;
  } catch (e) {
    if (e?.code === '1' || /cancel/i.test(e?.message || '')) return null; // user cancelled
    console.warn('[Billing] purchase failed', e);
    throw e;
  }
}

/** Restore any active subscriptions the signed-in Google account already owns. */
export async function restorePurchases() {
  if (!billingAvailable()) return [];
  try {
    const res = await GooglePlayBilling.restorePurchases();
    return res?.purchases || [];
  } catch (e) {
    console.warn('[Billing] restorePurchases failed', e);
    return [];
  }
}

/** Opens the Google Play subscription management screen (system browser). */
export async function openSubscriptionManagement(productId = PRODUCT_MONTHLY) {
  if (!billingAvailable()) return false;
  try {
    await GooglePlayBilling.openSubscriptionManagement({ productId });
    return true;
  } catch (e) {
    console.warn('[Billing] openSubscriptionManagement failed', e);
    return false;
  }
}
